import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Factory, Loader2 } from "lucide-react";

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = mode === "login" ? await login(email, password) : await register(name, email, password);
    setLoading(false);
    if (mode === "register") {
      if (res.ok) { setError(""); setInfo(res.message || "Kaydınız alındı. Yönetici onayı bekleniyor."); setMode("login"); }
      else setError(res.error);
      return;
    }
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  const inputCls =
    "w-full mt-2 mb-5 px-4 py-3 rounded-lg bg-[#131A26] border border-[#212D40] text-white outline-none focus:border-[#2563EB] transition-colors";
  const labelCls = "text-xs font-medium uppercase tracking-wider text-[#94A3B8]";

  return (
    <div className="min-h-screen flex bg-[#0B0F17]">
      <div
        className="hidden lg:flex flex-1 relative items-end p-12"
        style={{
          backgroundImage:
            "linear-gradient(rgba(11,15,23,0.55), rgba(11,15,23,0.95)), url(https://images.unsplash.com/photo-1644238017851-21f15062c213?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-lg bg-[#2563EB] flex items-center justify-center">
              <Factory className="w-6 h-6 text-white" />
            </div>
            <span className="font-head text-2xl font-bold tracking-wide">ATLANTA YAY SAN.TİC.LTD.ŞTİ.</span>
          </div>
          <h1 className="font-head text-5xl font-bold leading-tight mb-4">
            Malzeme İhtiyaç Planlama Sistemi
          </h1>
          <p className="text-[#94A3B8] text-lg">
            Tel, kumaş ve kraft kağıdı stoklarınızın hangi hafta biteceğini görün, siparişlerinizi
            zamanında verin.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-[480px] flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#2563EB] flex items-center justify-center">
              <Factory className="w-5 h-5 text-white" />
            </div>
            <span className="font-head text-xl font-bold">ATLANTA YAY SAN.TİC.LTD.ŞTİ.</span>
          </div>

          <div className="flex gap-2 mb-6 bg-[#131A26] border border-[#212D40] rounded-lg p-1">
            <button type="button" data-testid="tab-login" onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === "login" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`}>
              Giriş Yap
            </button>
            <button type="button" data-testid="tab-register" onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === "register" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`}>
              Üye Ol
            </button>
          </div>

          <h2 className="font-head text-3xl font-bold mb-1">{mode === "login" ? "Giriş Yap" : "Üye Ol"}</h2>
          <p className="text-[#94A3B8] text-sm mb-8">
            {mode === "login" ? "Hesabınıza erişmek için giriş yapın" : "Ad soyad, e-posta ve şifre ile kayıt olun"}
          </p>

          {mode === "register" && (
            <>
              <label className={labelCls}>Ad Soyad</label>
              <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className={inputCls} placeholder="Örn. Ahmet Yılmaz" required minLength={2} />
            </>
          )}

          <label className={labelCls}>E-posta</label>
          <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className={inputCls} placeholder="ornek@fabrika.com" required />

          <label className={labelCls}>Şifre</label>
          <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className={inputCls} placeholder="••••••••" required minLength={mode === "register" ? 6 : undefined} />

          {info && (
            <div data-testid="login-info"
              className="mb-4 text-sm text-[#10B981] bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.3)] rounded-lg px-4 py-3">
              {info}
            </div>
          )}
          {error && (
            <div data-testid="login-error"
              className="mb-4 text-sm text-[#EF4444] bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <button data-testid="login-submit-button" type="submit" disabled={loading}
            className="w-full py-3 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "login" ? "Giriş Yap" : "Üye Ol"}
          </button>

          {mode === "login" && (
            <div className="mt-8 text-xs text-[#64748B] bg-[#131A26] border border-[#212D40] rounded-lg p-4">
              Hesabınız yok mu? Üst taraftan <b className="text-[#94A3B8]">Üye Ol</b> ile ad soyad, e-posta ve şifre girerek kayıt olabilirsiniz.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
